import { useState, useCallback, useEffect, useRef } from 'react';
import { message } from 'antd';
import { draftStorage, isIndexedDBAvailable } from '@/utils/draftStorage';
import type { LaudoWizardData, LaudoStep } from '../types/laudo-wizard.types';

interface WizardDraftResult {
  data: LaudoWizardData;
  step: LaudoStep;
}

interface UseWizardDraftReturn {
  hasDraft: boolean;
  draftResult: WizardDraftResult | null;
  draftLoaded: boolean;
  available: boolean;
  saving: boolean;
  saveDraft: (data: LaudoWizardData, currentStep: LaudoStep) => Promise<void>;
  clearDraft: () => Promise<void>;
}

/**
 * Converte uma string base64 (com ou sem prefixo data:...) em Blob.
 */
function base64ToBlob(base64: string): Blob {
  const parts = base64.split(',');
  const mime = parts.length > 1 ? (parts[0].match(/:(.*?);/)?.[1] || 'image/jpeg') : 'image/jpeg';
  const raw = atob(parts.length > 1 ? parts[1] : parts[0]);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) {
    arr[i] = raw.charCodeAt(i);
  }
  return new Blob([arr], { type: mime });
}

/**
 * Hook para gerenciar rascunhos do wizard do laudo no IndexedDB.
 * Salva todos os dados do wizard + foto de fachada localmente.
 */
export function useWizardDraft(inspectionItemId: string | undefined): UseWizardDraftReturn {
  const [hasDraft, setHasDraft] = useState(false);
  const [draftResult, setDraftResult] = useState<WizardDraftResult | null>(null);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const available = useRef(isIndexedDBAvailable()).current;

  // Carregar draft no mount
  useEffect(() => {
    if (!available || !inspectionItemId) {
      setDraftLoaded(true);
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const draft = await draftStorage.getWizardDraft(inspectionItemId);
        if (cancelled) return;

        if (!draft) {
          setHasDraft(false);
          setDraftLoaded(true);
          return;
        }

        const wizardData = draft.wizardData as LaudoWizardData;

        // Restaurar foto de fachada do IndexedDB se marcada
        if ((wizardData.vistoria as any)?._hasDraftFotoFachada) {
          const fileEntry = await draftStorage.getWizardFile(inspectionItemId, 'fotoFachada');
          if (fileEntry && !cancelled) {
            wizardData.vistoria.fotoFachada = URL.createObjectURL(fileEntry.blob);
          }
          delete (wizardData.vistoria as any)._hasDraftFotoFachada;
        }

        if (!cancelled) {
          setDraftResult({ data: wizardData, step: draft.currentStep as LaudoStep });
          setHasDraft(true);
          setDraftLoaded(true);
        }
      } catch (error) {
        console.error('Erro ao carregar rascunho do wizard:', error);
        if (!cancelled) {
          setHasDraft(false);
          setDraftLoaded(true);
        }
      }
    };

    load();
    return () => { cancelled = true; };
  }, [available, inspectionItemId]);

  const saveDraft = useCallback(async (data: LaudoWizardData, currentStep: LaudoStep) => {
    if (!available || !inspectionItemId) return;

    setSaving(true);
    try {
      // Clonar dados para nao mutar o state original
      const cleanedData: any = JSON.parse(JSON.stringify(data));

      // Salvar foto de fachada no IndexedDB se for base64 ou blob
      const fotoFachada = data.vistoria.fotoFachada;
      if (fotoFachada && (fotoFachada.startsWith('data:') || fotoFachada.startsWith('blob:'))) {
        let blob: Blob;
        if (fotoFachada.startsWith('blob:')) {
          const response = await fetch(fotoFachada);
          blob = await response.blob();
        } else {
          blob = base64ToBlob(fotoFachada);
        }
        await draftStorage.saveWizardFile(inspectionItemId, 'fotoFachada', blob, 'foto-fachada.jpg');
        cleanedData.vistoria._hasDraftFotoFachada = true;
        // Limpar fotoFachada do JSON (foi salva como blob separado)
        cleanedData.vistoria.fotoFachada = undefined;
      }
      // Se for URL HTTP, manter no draft para não perder a referência
      cleanedData.vistoria.processedResponses = undefined;
      cleanedData.transmissao.processedLaudo = undefined;
      cleanedData.transmissao.logoBase64 = undefined;
      cleanedData.transmissao.pdfGerado = false;

      await draftStorage.saveWizardDraft(inspectionItemId, cleanedData, currentStep);
      setHasDraft(true);
      message.success('Rascunho salvo!');
    } catch (error) {
      console.error('Erro ao salvar rascunho do wizard:', error);
      message.error('Erro ao salvar rascunho');
    } finally {
      setSaving(false);
    }
  }, [available, inspectionItemId]);

  const clearDraft = useCallback(async () => {
    if (!available || !inspectionItemId) return;
    try {
      await draftStorage.deleteWizardDraft(inspectionItemId);
      setHasDraft(false);
      setDraftResult(null);
    } catch (error) {
      console.error('Erro ao limpar rascunho do wizard:', error);
    }
  }, [available, inspectionItemId]);

  return {
    hasDraft,
    draftResult,
    draftLoaded,
    available,
    saving,
    saveDraft,
    clearDraft,
  };
}
